import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AIModel, Message } from '@volt/shared';
import type { ApiClient } from '../api/client.js';
import { VoiceEngine, type VoiceStatus } from '../lib/voiceEngine.js';
import { pickVoiceModelId } from '../lib/voiceModel.js';
import { loadVoiceSettings, saveVoiceSettings, type VoiceSettings } from '../lib/voiceSettings.js';
import type { SendInput } from './useChat.js';

export interface UseVoiceConversationOptions {
  open: boolean;
  api: ApiClient;
  /** ISO-639-1 language of the UI, used as the speech-recognition hint. */
  language: string;
  /** The model chosen in the composer; the call may use a quicker one (see settings). */
  chatModelId: string | undefined;
  models: AIModel[];
  messages: Message[];
  streaming: boolean;
  send: (input: SendInput) => void;
  stop: () => void;
  /** Problems worth telling the user about (error code); `fatal` ends the conversation. */
  onProblem: (code: string, fatal: boolean) => void;
  onIdleTimeout: () => void;
}

export interface VoiceConversationState {
  status: VoiceStatus;
  /** Microphone loudness 0..1, drives the orb. */
  level: number;
  userText: string;
  assistantText: string;
  /** Set when the conversation cannot continue (e.g. microphone denied). */
  fatalCode: string | null;
  settings: VoiceSettings;
  updateSettings: (patch: Partial<VoiceSettings>) => void;
  /** The model the call actually uses (may differ from the composer's). */
  voiceModel: AIModel | undefined;
  /** Tap to talk over the assistant. */
  interrupt: () => void;
  /** Push-to-talk: the circle is pressed / released. */
  pressToTalk: () => void;
  releaseToTalk: () => void;
}

/** Runs a hands-free voice conversation on top of the normal chat while `open` is true. */
export function useVoiceConversation(options: UseVoiceConversationOptions): VoiceConversationState {
  const { open, api, language } = options;
  const [status, setStatus] = useState<VoiceStatus>('starting');
  const [level, setLevel] = useState(0);
  const [userText, setUserText] = useState('');
  const [assistantText, setAssistantText] = useState('');
  const [fatalCode, setFatalCode] = useState<string | null>(null);
  const [settings, setSettings] = useState<VoiceSettings>(loadVoiceSettings);
  const engineRef = useRef<VoiceEngine | null>(null);

  // Callbacks and chat state change on every render; the engine must not be rebuilt for that.
  const latest = useRef(options);
  latest.current = options;
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const voiceModelId = useMemo(
    () => pickVoiceModelId(options.models, options.chatModelId, settings.model),
    [options.models, options.chatModelId, settings.model],
  );
  const voiceModel = useMemo(
    () => options.models.find((model) => model.id === voiceModelId),
    [options.models, voiceModelId],
  );
  const voiceModelRef = useRef(voiceModel);
  voiceModelRef.current = voiceModel;

  useEffect(() => {
    if (!open) return;
    setStatus('starting');
    setLevel(0);
    setUserText('');
    setAssistantText('');
    setFatalCode(null);

    let lastLevelAt = 0;
    const engine = new VoiceEngine({
      api,
      language,
      getSettings: () => settingsRef.current,
      sendText: (text) => {
        const model = voiceModelRef.current;
        if (!model) return;
        latest.current.send({
          text,
          modelId: model.id,
          voice: true,
          // Only models that can search get the flag; the toggle is hidden for the others.
          webSearch: settingsRef.current.webSearch && model.supportsWebSearch ? true : undefined,
        });
      },
      stopGeneration: () => latest.current.stop(),
      callbacks: {
        onStatus: setStatus,
        onLevel: (value) => {
          const now = performance.now();
          if (now - lastLevelAt < 80) return; // ~12 fps is plenty for the orb
          lastLevelAt = now;
          setLevel(value);
        },
        onUserText: setUserText,
        onAssistantText: setAssistantText,
        onError: (code, fatal) => {
          if (fatal) setFatalCode(code);
          latest.current.onProblem(code, fatal);
        },
        onIdleTimeout: () => latest.current.onIdleTimeout(),
      },
    });
    engineRef.current = engine;
    engine.observe(latest.current.messages, latest.current.streaming);
    void engine.start();

    return () => {
      engine.destroy();
      engineRef.current = null;
    };
  }, [open, api, language]);

  useEffect(() => {
    engineRef.current?.observe(options.messages, options.streaming);
  }, [options.messages, options.streaming]);

  const updateSettings = useCallback((patch: Partial<VoiceSettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      saveVoiceSettings(next);
      settingsRef.current = next; // the engine reads it right away, before the re-render
      return next;
    });
    engineRef.current?.settingsChanged();
  }, []);

  return {
    status,
    level,
    userText,
    assistantText,
    fatalCode,
    settings,
    updateSettings,
    voiceModel,
    interrupt: () => engineRef.current?.interrupt(),
    pressToTalk: () => engineRef.current?.pressToTalk(),
    releaseToTalk: () => engineRef.current?.releaseToTalk(),
  };
}
